import { ElementProps } from '@/components/sections/builder/types';
import { useSearchParams } from 'next/navigation';

import CardProductBuilder from '@/components/sections/builder/elements/body/card-product';
import { PaginationProductgrid } from '@/components/sections/builder/elements/body/collection-product-grid/pagination';
import { SearchPageElementDataType } from '@/components/sections/builder/elements/body/search/type';
import CardProductSkeleton from '@/components/sections/card-loading';
import { useListProduct } from '@/hooks/api/storefront/product';
import { useDebounceValue } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';
import React from 'react';
import Cookies from 'js-cookie';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { usePaginationSearchParams } from '@/hooks/use-pagination';

export type FilterSortType = {
  inStock: boolean;
  outStock: boolean;
  fromPrice: string;
  toPrice: string;
  v?: { availability: number[] };
};

export default function ElementContent({ element, preview, theme }: ElementProps) {
  const query = useSearchParams();
  const customerToken = Cookies.get('accessToken');

  const data = element.data as SearchPageElementDataType;
  const [pagination, setPagination] = usePaginationSearchParams();

  const getKeyword = query.get('keyword') || '';
  const debounceKeyword = useDebounceValue(getKeyword, 300);

  const { data: searchData, isLoading } = useListProduct(
    { page: pagination.pageIndex + 1, pageSize: data.limit || 20, q: debounceKeyword },
    customerToken as string
  );

  const products = searchData?.data || [];
  const totalPages = Math.ceil(Number(searchData?.totalCount) / (data.limit || 20));

  React.useEffect(() => {
    if (!preview && products.length > 0) {
      SendEventAnalytic('view_search_results', {
        search_string: debounceKeyword,
      });

      SendEventMetapixel('Search', {
        content_ids: products.map((product) => product.id),
        content_type: 'product',
        contents: products.map((product) => ({
          id: product.id,
          name: product.product_name,
          quantity: product.available_qty,
        })),
        currency: 'IDR',
        search_string: debounceKeyword,
      });
    }
  }, [products, preview]);

  return (
    <>
      <section className={cn('bg_product_grid_color_scheme')}>
        <div className='theme_layout_page_width mx-auto duration-100'>
          <div className={cn('product-list-padding relative flex')}>
            <div
              className={cn(
                'relative w-full px-2 py-2 lg:px-6 lg:py-6',
                !isLoading && products.length === 0 && 'flex min-h-[calc(100vh_-_250px)]'
              )}
            >
              <div
                data-testid='search-content'
                className={cn(
                  'flex flex-col',
                  data.contentAlign === 'left' && '[&>*]:text-left',
                  data.contentAlign === 'center' && '[&>*]:text-center',
                  data.contentAlign === 'right' && '[&>*]:text-right'
                )}
              >
                <div className='w-full'>
                  <div
                    className={cn(
                      `grid-cols-${data.totalColMobile} sm:grid-cols-${data.totalCol} md:grid-cols-${data.totalCol} lg:grid-cols-${data.totalCol} theme_layout_space_grid_horizontal theme_layout_space_grid_vertical grid place-content-center`
                    )}
                  >
                    {isLoading
                      ? Array.from(Array(20).keys()).map((e) => <CardProductSkeleton key={e} />)
                      : products.map((product) => (
                          <CardProductBuilder
                            data={data}
                            product={product}
                            key={product.id}
                            element={element}
                            preview={preview}
                            theme={theme}
                          />
                        ))}
                  </div>
                  {!isLoading && searchData?.data?.length === 0 && (
                    <div className='w-full flex-col text-center'>
                      <h2 className='my-10 text-2xl font-light text-black'>
                        No products found <br />
                      </h2>
                    </div>
                  )}
                  {!isLoading && products.length > 0 ? (
                    <PaginationProductgrid
                      handlePageChange={(newPage) => setPagination({ pageIndex: newPage - 1 })}
                      page={pagination.pageIndex + 1}
                      totalPages={totalPages}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <style jsx>
        {`
          @media (min-width: 768px) {
            .product-list-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
            .full_width_product_list {
              width: calc(100vw - 20px);
              margin-left: calc(-50vw + 50% + 10px);
            }
          }
          .card-rounded {
            border-radius: ${data.rounded || 0}px;
          }

          .bg_product_grid_color_scheme {
            background-color: ${data.colorScheme?.background || 'white'};
            color: ${data.colorScheme?.text || '52535B'};
          }
          .background-color {
            background-color: ${data.background?.backgroundColor};
            --tw-bg-opacity: ${data.background?.backgroundOpacity};
          }
          .background-image {
            background-image: url(${data.background?.backgroundImage});
            background-repeat: no-repeat;
            background-size: ${data.background?.backgroundSize};
            background-position: ${data.background?.backgroundPosition};
            background-attachment: ${data.background?.backgroundAttachment};
          }
        `}
      </style>
    </>
  );
}

// .overlay {
//   background-color: ${data.overlay};
// }
